/**
 * Dataset semantic layer — Phase 1 metadata annotations.
 *
 * Background: ADR-001. We hold a vendor-neutral metadata block on each
 * dataset describing its semantic shape (joins, dimensions, measures,
 * row-level security predicates). Phase 1 the metadata feeds our CQS
 * compiler + governance UI directly; Phase 2 it generates a Cube.js
 * schema. The shape here is intentionally a strict subset of Cube's
 * schema so the Phase 2 migration is mechanical.
 *
 * Authored either via the web app's dataset detail page or via MCP
 * tools (`update_dataset_semantic`). Validated by the API on write.
 *
 * Stability: changes to this schema are versioned via
 * AuthoringStandards.semantic.version. Existing datasets keep their
 * stored block unmodified across schema bumps; the validator runs
 * on read with a `lenient` mode for backward compatibility.
 */
import { z } from 'zod';
// ─── Identifier shape — same predicate everywhere ────────────────────
const identifierSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/u, {
    message: 'Identifier must match /^[a-zA-Z_][a-zA-Z0-9_]*$/',
});
// ─── Joins ────────────────────────────────────────────────────────────
//
// `to` names another dataset (by name) inside the same tenant. `on` is
// a SQL boolean predicate referencing columns on both sides; the
// compiler validates it's a balanced predicate at write time.
export const semanticJoinSchema = z.object({
    to: identifierSchema.describe('Name of the dataset to join to (must exist in tenant).'),
    on: z
        .string()
        .min(3)
        .max(500)
        .describe('SQL predicate, e.g. "locations.id = stores.location_id".'),
    kind: z
        .enum(['inner', 'left'])
        .default('left')
        .describe('Join kind. "left" by default to preserve left-side rows.'),
    description: z.string().max(500).optional(),
});
// ─── Dimensions ───────────────────────────────────────────────────────
export const dimensionTypeSchema = z.enum(['string', 'number', 'boolean', 'time', 'geo']);
export const semanticDimensionSchema = z.object({
    name: identifierSchema,
    type: dimensionTypeSchema,
    /** SQL expression — column reference or simple expression. The
     *  compiler restricts this to identifiers + arithmetic + CASE; no
     *  subqueries, no UDFs. */
    sql: z.string().min(1).max(500),
    /** Allowed time grains for time dimensions. Ignored for non-time. */
    grains: z.array(z.enum(['minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'])).optional(),
    /** Whether this dimension can be used as a primary key for joins
     *  from other datasets. At most one dimension per dataset may be the
     *  primary key. */
    isPrimaryKey: z.boolean().default(false),
    description: z.string().max(500).optional(),
    /** PII flag — masked in AI samples. Mirrors the column-level pii
     *  flag on adapter.ts but at the dimension level. */
    pii: z.boolean().default(false),
});
// ─── Measures ─────────────────────────────────────────────────────────
export const measureTypeSchema = z.enum([
    'count',
    'count_distinct',
    'sum',
    'avg',
    'min',
    'max',
    // Computed measures are SQL expressions composed from other measures
    // (e.g. "active_stores / total_stores * 100"). The compiler enforces
    // they only reference declared measures, never raw columns.
    'computed',
]);
export const semanticMeasureSchema = z.object({
    name: identifierSchema,
    type: measureTypeSchema,
    sql: z.string().min(1).max(500),
    /** Additivity matters for correct rollup arithmetic. `count`, `sum`,
     *  `min`, `max` are additive; `count_distinct`, `avg` are not.
     *  Defaulted by the compiler from `type` but overridable for
     *  `computed`. */
    additive: z.boolean().optional(),
    /** Display format hint for UI: passes through to the number-format
     *  registry. */
    format: z.enum(['number', 'currency', 'percent', 'duration_ms']).optional(),
    description: z.string().max(500).optional(),
});
// ─── Row-level security ──────────────────────────────────────────────
//
// Each rule names a role and provides a SQL predicate. The predicate
// MAY reference $tenantId and $claims.<key> placeholders that the
// runtime substitutes from the verified JWT. Multiple rules for the
// same role are AND-combined; rules across roles are OR-combined when
// a user has multiple roles.
export const semanticRlsRuleSchema = z.object({
    role: identifierSchema.describe('Platform role name this rule applies to.'),
    filter: z
        .string()
        .min(3)
        .max(500)
        .describe('SQL predicate. May reference $tenantId and $claims.<key> placeholders.'),
    description: z.string().max(500).optional(),
});
// ─── Top-level semantic block ─────────────────────────────────────────
export const datasetSemanticSchema = z
    .object({
    /** Schema version. Bumps when we make breaking changes; the
     *  validator falls back to lenient for older versions. */
    version: z.literal(1).default(1),
    primaryKey: identifierSchema
        .optional()
        .describe('Convenience shorthand: marks one dimension as the dataset PK ' +
        'without spelling out isPrimaryKey on the dimension itself. ' +
        'Equivalent to setting isPrimaryKey=true on that dimension.'),
    joins: z.array(semanticJoinSchema).max(8).default([]),
    dimensions: z.array(semanticDimensionSchema).max(64).default([]),
    measures: z.array(semanticMeasureSchema).max(64).default([]),
    rls: z.array(semanticRlsRuleSchema).max(16).default([]),
    /** Free-form notes for human authors — never consumed by the
     *  compiler. */
    notes: z.string().max(2000).optional(),
})
    .superRefine((doc, ctx) => {
    // Cross-field invariants
    const dimNames = new Set();
    let pkCount = 0;
    for (const d of doc.dimensions) {
        if (dimNames.has(d.name)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dimensions'],
                message: `Duplicate dimension name: ${d.name}`,
            });
        }
        dimNames.add(d.name);
        if (d.isPrimaryKey)
            pkCount++;
        if (d.type === 'time' && (!d.grains || d.grains.length === 0)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dimensions'],
                message: `Time dimension "${d.name}" must declare at least one grain.`,
            });
        }
    }
    if (doc.primaryKey && !dimNames.has(doc.primaryKey)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['primaryKey'],
            message: `primaryKey "${doc.primaryKey}" does not match any declared dimension.`,
        });
    }
    if (pkCount > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dimensions'],
            message: 'At most one dimension may be marked isPrimaryKey.',
        });
    }
    const measureNames = new Set();
    for (const m of doc.measures) {
        if (measureNames.has(m.name)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['measures'],
                message: `Duplicate measure name: ${m.name}`,
            });
        }
        measureNames.add(m.name);
        if (m.type !== 'computed' && (!m.sql || m.sql.trim().length === 0)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['measures'],
                message: `Measure "${m.name}" of type ${m.type} requires sql.`,
            });
        }
    }
    // Dim/measure namespaces must not collide.
    for (const m of doc.measures) {
        if (dimNames.has(m.name)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['measures'],
                message: `Measure "${m.name}" collides with a dimension of the same name.`,
            });
        }
    }
});
/**
 * Catalog spec exposed via `GET /authoring/standards`. Lets MCP clients
 * + the web Copilot author against the contract without re-deriving it
 * from the Zod schema.
 */
export const datasetSemanticCatalog = {
    version: 1,
    description: 'Vendor-neutral semantic layer block attached to each dataset. ' +
        'Joins, dimensions, measures, and row-level security predicates. ' +
        'Phase 1 metadata; Phase 2 (CSAGE-39) compiles to Cube.js schema.',
    dimensionTypes: ['string', 'number', 'boolean', 'time', 'geo'],
    timeGrains: ['minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'],
    measureTypes: ['count', 'count_distinct', 'sum', 'avg', 'min', 'max', 'computed'],
    joinKinds: ['inner', 'left'],
    rlsPlaceholders: ['$tenantId', '$claims.<key>'],
    limits: {
        maxJoins: 8,
        maxDimensions: 64,
        maxMeasures: 64,
        maxRlsRules: 16,
    },
};
//# sourceMappingURL=semantic.js.map